/**
 * The menu shown after drawing a link between two nodes: which kind of link to create, with
 * the likely kind suggested (and focused, so Enter accepts it).
 */
import { For, Show, type JSX } from "solid-js";
import type { LinkKindView } from "../../shared/kinds";
import type { SimNode } from "../../state/scene";

/** Where the menu is and which nodes it links. */
export interface LinkMenuState {
  from: SimNode;
  to: SimNode;
  /** Screen point (relative to the canvas) where the link was released. */
  x: number;
  y: number;
}

/** The link-kind menu (every kind in `kinds`, with `suggested` first in focus), kept inside a `width` × `height` area. */
export function LinkMenu(props: { menu: LinkMenuState; kinds: LinkKindView[]; width: number; height: number; suggested: string | null; onChoose: (kind: string) => void }): JSX.Element {
  /** The likely link kind for these two nodes (picked by the caller from the kinds' rules). */
  const suggested = () => props.suggested;
  /** Left edge, kept inside the canvas. */
  const left = () => Math.max(8, Math.min(props.menu.x + 8, props.width - 210));
  /** Top edge, kept inside the canvas. */
  const top = () => Math.max(8, Math.min(props.menu.y + 8, props.height - 40 - 34 * props.kinds.length));
  return (
    <div class="lmenu" role="dialog" aria-label="Choose link type" style={{ left: `${left()}px`, top: `${top()}px` }}>
      <div class="lm-h">
        <b>{props.menu.from.node.name}</b> to <b>{props.menu.to.node.name}</b>
      </div>
      <For each={props.kinds}>
        {(kind) => (
          <button
            type="button"
            class="lm-b"
            classList={{ sug: kind.id === suggested() }}
            data-link-kind={kind.id}
            title={kind.note || undefined}
            ref={(el) => kind.id === suggested() && queueMicrotask(() => el.focus())}
            onClick={() => props.onChoose(kind.id)}
          >
            {kind.label}
            <Show when={kind.id === suggested()}>
              <em>suggested</em>
            </Show>
          </button>
        )}
      </For>
    </div>
  );
}
